import { Info, Printer, Snowflake } from 'lucide-react'
import { useEquipmentScope } from './equipmentScope'
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { equipmentScanCode } from '@/lib/equipmentLabels'
import { EquipmentDocumentsPanel } from './EquipmentDocumentsPanel'
import type { Equipment } from '@/types/equipment'

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className='space-y-1'>
      <p className='text-xs font-medium text-muted-foreground'>{label}</p>
      <div className='rounded-md border border-gray-200 bg-gray-50/60 px-3 py-2 text-sm text-gray-800'>
        {value}
      </div>
    </div>
  )
}

export function EquipmentInfoModal({
  equipment,
  onClose,
  onEdit,
  onPrintLabel,
  onConfigureStorage,
}: {
  equipment: Equipment | null
  onClose: () => void
  onEdit?: (equipment: Equipment) => void
  /**
   * Preview and print this unit's asset tag.
   *
   * Lives here rather than as its own grid column: the scan code is one more
   * fact about the machine, alongside model, serial and vendor, and a column
   * per action does not scale on a grid already 1400px wide. It is also where
   * someone looks when they are working out which machine they are holding.
   */
  onPrintLabel?: (equipment: Equipment) => void
  /**
   * Jump to the Storage tab for this unit. A callback rather than a nested
   * dialog: layout work needs the whole page, and stacking a second dialog on
   * this one traps focus in the wrong place.
   */
  onConfigureStorage?: (equipment: Equipment) => void
}) {
  const { copy } = useEquipmentScope()
  return (
    <Dialog open={equipment !== null} onOpenChange={(open) => !open && onClose()}>
      {/* See EditEquipmentModal: the document list has no length bound. */}
      <DialogContent className='max-h-[90vh] overflow-y-auto sm:max-w-lg'>
        {equipment && (
          <>
            <div className='flex items-start gap-3'>
              <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600'>
                <Info className='h-5 w-5' />
              </div>
              <div className='min-w-0'>
                <h2 className='text-base font-semibold text-gray-900'>{equipment.equipmentId}</h2>
                <p className='mt-0.5 text-xs text-muted-foreground'>
                  {equipment.equipmentType} · {equipment.group}
                </p>
              </div>
            </div>

            <div className='mt-4 grid grid-cols-2 gap-3'>
              <Field label='Model' value={equipment.model} />
              <Field label='Serial' value={equipment.serial} />
              <Field label='Vendor' value={equipment.vendor} />
              <Field label='Installed' value={String(equipment.installed)} />
            </div>
            <div className='mt-3'>
              <Field label='Location' value={equipment.location.display} />
            </div>

            {equipment.purpose === 'STORAGE' && onConfigureStorage && (
              <div className='mt-3'>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => onConfigureStorage(equipment)}
                  className='gap-2'
                >
                  <Snowflake className='h-4 w-4' />
                  Storage layout
                </Button>
              </div>
            )}

            {/* The scan code is a fact about the machine, so it sits with the
                other asset details — and seeing it here is how someone confirms
                the tag on the box matches the unit they are looking at. */}
            <div className='mt-3 rounded-md border p-3'>
              <div className='flex items-start justify-between gap-3'>
                <div className='min-w-0'>
                  <p className='text-[11px] font-semibold uppercase tracking-wider text-muted-foreground'>
                    Asset tag
                  </p>
                  <code className='mt-1 block select-all break-all font-mono text-sm font-semibold'>
                    {equipmentScanCode(equipment.id) ?? '—'}
                  </code>
                </div>
                {onPrintLabel && (
                  <Button
                    variant='outline'
                    size='sm'
                    className='shrink-0 gap-1.5'
                    onClick={() => onPrintLabel(equipment)}
                  >
                    <Printer className='h-4 w-4' />
                    Print
                  </Button>
                )}
              </div>
              <p className='mt-2 text-[11px] leading-relaxed text-muted-foreground'>
                {copy.tagHint}
              </p>
            </div>

            {/*
              Read-only here. This dialog is the "what is this machine" view —
              someone standing at it wants the manual, not an upload control.
              Managing them is the Edit dialog's job, which is also where the
              permission to do so is already established.
            */}
            <div className='mt-3'>
              <EquipmentDocumentsPanel owner={{ kind: 'unit', id: equipment.id }} readOnly />
            </div>

            <DialogFooter className='mt-5'>
              {onEdit && (
                <Button variant='outline' onClick={() => onEdit(equipment)}>
                  Edit
                </Button>
              )}
              <Button onClick={onClose}>Close</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
